import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { useAppStore } from '../store/appStore';
import type { Campaign } from '../store/appStore';

interface SuggestionListProps {
  fullscreen?: boolean;
}

export const SuggestionList: React.FC<SuggestionListProps> = ({ fullscreen = false }) => {
  const navigate = useNavigate();
  const { currentAccount, toggleUsed, removeAllSuggestions, fetchCampaignsByAccount } = useAppStore();
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [campaignMap, setCampaignMap] = useState<Record<string, Campaign>>({});

  useEffect(() => {
    if (currentAccount) {
      fetchCampaignsByAccount(currentAccount.id)
        .then(campaigns => {
          const map: Record<string, Campaign> = {};
          for (const c of campaigns) map[c.id] = c;
          setCampaignMap(map);
        })
        .catch(() => {});
    }
  }, [currentAccount?.id]);

  if (!currentAccount) return null;

  const allSuggestions = currentAccount.suggestions;
  const total = allSuggestions.length;
  const hasSuggestions = total > 0;
  const PREVIEW_LIMIT = 3;
  const suggestions = fullscreen ? allSuggestions : allSuggestions.slice(0, PREVIEW_LIMIT);
  const hasMore = !fullscreen && total > PREVIEW_LIMIT;

  const handleRemoveAll = async () => {
    try {
      await removeAllSuggestions();
      setShowConfirmDelete(false);
    } catch (error) {
      console.error('Failed to remove suggestions:', error);
    }
  };

  const formatTime = (createdAt: string) =>
    new Date(createdAt).toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' });
  const formatDate = (createdAt: string) =>
    new Date(createdAt).toLocaleDateString('no-NO', { day: 'numeric', month: 'short' });

  const inner = (
    <div className={fullscreen ? 'p-8' : 'p-6'}>
      <div className="flex justify-between items-center mb-4">
        <h2 className={`font-semibold text-white ${fullscreen ? 'text-3xl' : 'text-xl'}`}>
          Forslag ({total})
        </h2>
        <div className="flex gap-2">
          {hasSuggestions && !showConfirmDelete && (
            <Button
              onClick={() => setShowConfirmDelete(true)}
              className="bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm"
            >
              Slett alle
            </Button>
          )}
          {showConfirmDelete && (
            <>
              <Button
                onClick={() => setShowConfirmDelete(false)}
                className="bg-white/10 hover:bg-white/20 text-white text-sm"
              >
                Avbryt
              </Button>
              <Button
                onClick={handleRemoveAll}
                className="bg-red-500 hover:bg-red-600 text-white text-sm"
              >
                Bekreft
              </Button>
            </>
          )}
        </div>
      </div>

      <div className={`space-y-2 ${fullscreen ? 'overflow-y-auto' : ''}`} style={fullscreen ? { maxHeight: 'calc(100vh - 140px)' } : {}}>
        {hasSuggestions ? (
          suggestions.map((suggestion, i) => {
            const number = total - i;
            const campaign = suggestion.campaignId ? campaignMap[suggestion.campaignId] : null;
            return (
              <div
                key={suggestion.id}
                className={`flex items-start justify-between rounded-lg ${
                  fullscreen ? 'p-4' : 'p-3'
                } ${suggestion.used ? 'bg-[#2a2a2a40]' : 'bg-[#2a2a2a]'}`}
              >
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <span className={`shrink-0 font-bold tabular-nums ${
                    fullscreen ? 'text-2xl text-white/40 w-10' : 'text-sm text-white/40 w-6'
                  }`}>
                    #{number}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`${suggestion.used ? 'text-gray-500 line-through' : 'text-white'} ${fullscreen ? 'text-xl' : ''}`}>
                      {suggestion.message}
                    </p>
                    <div className={`flex flex-wrap items-center gap-x-2 mt-1 ${fullscreen ? 'text-sm' : 'text-xs'}`}>
                      <span className="text-gray-500">
                        {suggestion.submittedBy && `${suggestion.submittedBy} · `}
                        {formatDate(suggestion.createdAt)} · {formatTime(suggestion.createdAt)}
                      </span>
                      {campaign && (
                        <button
                          onClick={() => navigate(`/dashboard/campaign/${campaign.id}`)}
                          className="text-blue-400 hover:text-blue-300 transition-colors truncate max-w-[200px]"
                        >
                          {campaign.title}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {!fullscreen && (
                  <Button
                    onClick={() => toggleUsed(suggestion.id)}
                    className={`ml-3 shrink-0 text-xs ${
                      suggestion.used
                        ? 'bg-white/5 hover:bg-white/10 text-gray-400'
                        : 'bg-white hover:bg-gray-200 text-black'
                    }`}
                  >
                    {suggestion.used ? 'Angre' : 'Brukt'}
                  </Button>
                )}
              </div>
            );
          })
        ) : (
          <p className={`text-gray-500 text-center py-8 ${fullscreen ? 'text-xl' : ''}`}>
            Ingen forslag enda. Del QR-koden for å komme i gang!
          </p>
        )}
      </div>

      {hasMore && (
        <p className="text-center text-gray-500 text-xs mt-3">
          Viser {PREVIEW_LIMIT} av {total}. Åpne fullskjerm for å se alle.
        </p>
      )}
    </div>
  );

  if (fullscreen) {
    return <div className="bg-[#0a0a0a] min-h-screen">{inner}</div>;
  }

  return (
    <Card className="bg-[#1a1a1a] border-none">
      <CardContent className="p-0">{inner}</CardContent>
    </Card>
  );
};
